import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import UserProfileMenu from './UserProfileMenu';
import { useAuth } from '../hooks/useAuth';
import { BRAND_SERVICE_VIEW } from '@/brand';
import '../styles/appShell.css';

// ────────────────────────────────────────────────────────────────────────────
// The signed-in page frame.
//
// Every authenticated page used to draw its own frame, and they had drifted into
// four different backgrounds (#EBEBEF, #F3F4F6, #F5F6F8 and the Dashboard's warm
// gradient), three header layouts and six content widths. This is the single
// definition: same background as the Dashboard, same sidebar, same header row,
// same content column. Pages supply a title and their body, nothing else.
//
// Brand-aware, like the Dashboard: blue-and-yellow on connectcorporate.com,
// cool blue on conncct.com. Each domain is single-product so the tone is fixed
// per load. See styles/appAccent.css for why blue and yellow are not
// interchangeable here — yellow is a fill only, and its ink is navy.
// ────────────────────────────────────────────────────────────────────────────

const IS_CONSULTING = BRAND_SERVICE_VIEW === 'consulting';

/** Flat page colour, behind the sidebar and below the scrolling main column. */
export const SHELL_BG = IS_CONSULTING ? '#EFF4FB' : '#FCFAF2';

/** The Dashboard's top-down wash. Verbatim, so the pages truly match it. */
export const SHELL_GRADIENT = IS_CONSULTING
  ? 'linear-gradient(180deg,#E9F1FE 0%,#F1F5FB 30%,#EFF4FB 100%)'
  : 'linear-gradient(180deg,#FFF8E3 0%,#FCFAF1 30%,#FCFAF2 100%)';

/** Brand accent, for anything a page paints itself (buttons, active states).
 *  Safe as text and as a fill under white ink on both brands. */
export const ACCENT      = IS_CONSULTING ? '#1976D2' : '#2159B8';
export const ACCENT_DEEP = IS_CONSULTING ? '#1565C0' : '#17408A';
/** Tinted surfaces and hairlines — yellow on corporate, blue on consulting. */
export const ACCENT_SOFT = IS_CONSULTING ? '#EFF6FF' : '#FFF3CE';
export const ACCENT_LINE = IS_CONSULTING ? '#BFDBFE' : '#F0DDA4';
/** Text colour ON a tinted surface. Deep gold, because the brand yellow reads
 *  at 1.2:1 there. */
export const ACCENT_ON_SOFT = IS_CONSULTING ? '#1E4E8C' : '#7A5E00';

/** The brand's gold layer. A FILL ONLY — paint text on it with GOLD_INK, never
 *  white, and never use GOLD as a text colour on a light surface. */
export const GOLD      = IS_CONSULTING ? '#1976D2' : '#FFD54F';
export const GOLD_DEEP = IS_CONSULTING ? '#1565C0' : '#E6B400';
export const GOLD_INK  = IS_CONSULTING ? '#fff' : '#17263A';

type Props = {
  /** Page title. Rendered at the Dashboard's h1 size. */
  title?: ReactNode;
  /** One line under the title. */
  subtitle?: ReactNode;
  /** Trailing crumb after "Dashboard /". Omit for no breadcrumb. */
  crumb?: ReactNode;
  /** Controls sitting to the left of the profile menu in the header row. */
  actions?: ReactNode;
  /** Content column width. Defaults to the Dashboard's 1400. */
  maxWidth?: number;
  /** Replaces the whole body with a centred spinner. */
  loading?: boolean;
  onLogout?: () => void;
  children?: ReactNode;
};

export default function AppShell({
  title,
  subtitle,
  crumb,
  actions,
  maxWidth = 1400,
  loading = false,
  onLogout,
  children,
}: Props) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = onLogout ?? (() => { logout(); navigate('/'); });

  return (
    <div className="appshell flex min-h-screen" style={{ background: SHELL_BG }}>
      <Sidebar onLogout={handleLogout} />
      <main
        className="flex-1 md:ms-64 overflow-y-auto relative"
        style={{ background: SHELL_GRADIENT }}
      >
        <MobileNav onLogout={handleLogout} />

        <div className="mx-auto w-full px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8" style={{ maxWidth }}>
          {crumb && (
            <div className="mb-2 flex items-center gap-1.5 text-sm text-gray-500">
              <button onClick={() => navigate('/dashboard')} className="hover:text-gray-900 transition">
                Dashboard
              </button>
              <span className="text-gray-300">/</span>
              <span className="font-medium text-gray-900">{crumb}</span>
            </div>
          )}

          {/* Rendered whenever there is anything to put in it — the profile menu
              alone still needs its row, so pages never re-place it themselves. */}
          <header className="mb-6 flex justify-between items-start gap-4">
            <div className="min-w-0">
              {title && <h1 className="text-2xl font-bold text-gray-900">{title}</h1>}
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {actions}
              <UserProfileMenu />
            </div>
          </header>

          {loading
            ? (
              <div className="flex justify-center py-24">
                <div
                  className="w-10 h-10 border-4 rounded-full animate-spin"
                  style={{ borderColor: ACCENT, borderTopColor: 'transparent' }}
                />
              </div>
            )
            : children}
        </div>
      </main>
    </div>
  );
}
